import { Type, type Static } from "@sinclair/typebox";
import { NonEmptyString, SessionLabelString } from "./primitives.js";

export const SessionsListParamsSchema = Type.Object(
  {
    limit: Type.Optional(Type.Integer({ minimum: 1 })),
    activeMinutes: Type.Optional(Type.Integer({ minimum: 1 })),
    includeGlobal: Type.Optional(Type.Boolean()),
    includeUnknown: Type.Optional(Type.Boolean()),
    /**
     * Read first 8KB of each session transcript to derive title from first user message.
     * Performs a file read per session - use `limit` to bound result set on large stores.
     */
    includeDerivedTitles: Type.Optional(Type.Boolean()),
    /**
     * Read last 16KB of each session transcript to extract most recent message preview.
     * Performs a file read per session - use `limit` to bound result set on large stores.
     */
    includeLastMessage: Type.Optional(Type.Boolean()),
    label: Type.Optional(SessionLabelString),
    spawnedBy: Type.Optional(NonEmptyString),
    agentId: Type.Optional(NonEmptyString),
    search: Type.Optional(Type.String()),
  },
  { additionalProperties: false },
);

export const SessionsPreviewParamsSchema = Type.Object(
  {
    keys: Type.Array(NonEmptyString, { minItems: 1 }),
    limit: Type.Optional(Type.Integer({ minimum: 1 })),
    maxChars: Type.Optional(Type.Integer({ minimum: 20 })),
  },
  { additionalProperties: false },
);

export const SessionsResolveParamsSchema = Type.Object(
  {
    key: Type.Optional(NonEmptyString),
    sessionId: Type.Optional(NonEmptyString),
    label: Type.Optional(SessionLabelString),
    agentId: Type.Optional(NonEmptyString),
    spawnedBy: Type.Optional(NonEmptyString),
    includeGlobal: Type.Optional(Type.Boolean()),
    includeUnknown: Type.Optional(Type.Boolean()),
  },
  { additionalProperties: false },
);

export const SessionsPatchParamsSchema = Type.Object(
  {
    key: NonEmptyString,
    label: Type.Optional(Type.Union([SessionLabelString, Type.Null()])),
    displayName: Type.Optional(Type.Union([Type.String({ minLength: 1 }), Type.Null()])),
    thinkingLevel: Type.Optional(Type.Union([NonEmptyString, Type.Null()])),
    verboseLevel: Type.Optional(Type.Union([NonEmptyString, Type.Null()])),
    reasoningLevel: Type.Optional(Type.Union([NonEmptyString, Type.Null()])),
    responseUsage: Type.Optional(
      Type.Union([
        Type.Literal("off"),
        Type.Literal("tokens"),
        Type.Literal("full"),
        // Backward compat with older clients/stores.
        Type.Literal("on"),
        Type.Null(),
      ]),
    ),
    elevatedLevel: Type.Optional(Type.Union([NonEmptyString, Type.Null()])),
    execHost: Type.Optional(Type.Union([NonEmptyString, Type.Null()])),
    execSecurity: Type.Optional(Type.Union([NonEmptyString, Type.Null()])),
    execAsk: Type.Optional(Type.Union([NonEmptyString, Type.Null()])),
    execNode: Type.Optional(Type.Union([NonEmptyString, Type.Null()])),
    model: Type.Optional(Type.Union([NonEmptyString, Type.Null()])),
    spawnedBy: Type.Optional(Type.Union([NonEmptyString, Type.Null()])),
    /**
     * Manual override of the chat-history sidebar preview line (the gray
     * "fleet-chat-history__item-preview muted" snippet under each row).
     * Distinct from `label`: the title and preview are independent fields.
     * Pass a non-empty string to set, or null to clear (the next chat.send
     * on this session will then re-mint the preview from the first user
     * prompt). Capped at 400 chars (matches the field on SessionEntry).
     */
    firstUserPreview: Type.Optional(
      Type.Union([Type.String({ minLength: 1, maxLength: 400 }), Type.Null()]),
    ),
    sendPolicy: Type.Optional(
      Type.Union([Type.Literal("allow"), Type.Literal("deny"), Type.Null()]),
    ),
    groupActivation: Type.Optional(
      Type.Union([Type.Literal("mention"), Type.Literal("always"), Type.Null()]),
    ),
  },
  { additionalProperties: false },
);

export const SessionsResetParamsSchema = Type.Object(
  {
    key: NonEmptyString,
    /**
     * Explicit confirmation that the caller intends to discard the existing
     * transcript and replace it with a fresh session. Required for the
     * destructive pass; omit (or set to false) to receive a
     * CONFIRMATION_REQUIRED error that lists what would be changed.
     */
    confirm: Type.Optional(Type.Boolean()),
    /**
     * When true, the server returns the planned reset (what would change)
     * without mutating any state. Useful as a UI preview before the user
     * confirms a destructive reset.
     */
    dryRun: Type.Optional(Type.Boolean()),
  },
  { additionalProperties: false },
);

export const SessionsReorderParamsSchema = Type.Object(
  {
    /** Ordered list of session keys in the desired user-facing order (left → right). */
    keys: Type.Array(NonEmptyString, { minItems: 1, maxItems: 500 }),
  },
  { additionalProperties: false },
);

export const SessionsReorderResultSchema = Type.Object(
  {
    /** Number of session entries that were actually updated. */
    updated: Type.Integer({ minimum: 0 }),
    /** Keys that were not found in the store and therefore not reordered. */
    skipped: Type.Array(NonEmptyString),
  },
  { additionalProperties: false },
);

export const SessionsDeleteParamsSchema = Type.Object(
  {
    key: NonEmptyString,
    deleteTranscript: Type.Optional(Type.Boolean()),
  },
  { additionalProperties: false },
);

export const SessionsSpawnParamsSchema = Type.Object(
  {
    /** Parent session that receives the subagent handoff summary */
    sessionKey: NonEmptyString,
    task: NonEmptyString,
    label: Type.Optional(Type.String()),
    agentId: Type.Optional(NonEmptyString),
    model: Type.Optional(NonEmptyString),
    thinking: Type.Optional(Type.String()),
    runTimeoutSeconds: Type.Optional(Type.Number({ minimum: 0 })),
    timeoutSeconds: Type.Optional(Type.Number({ minimum: 0 })),
    cleanup: Type.Optional(Type.Union([Type.Literal("delete"), Type.Literal("keep")])),
    /** Bypass unified-planner direct-spawn guard for operator UI orchestration. */
    spawnSource: Type.Optional(Type.Union([Type.Literal("planner"), Type.Literal("direct")])),
  },
  { additionalProperties: false },
);

/**
 * JAISIU-3319 / Pillar H: cancel an in-flight subagent run identified by its
 * child session key. The RPC aborts the embedded run loop, finalizes the
 * registry record with `outcome.status = "cancelled"`, and (when
 * `broadcast !== false`) emits a `chat.final` to the parent with
 * `status: "cancelled"` so the orchestration group reaches a settled state.
 */
export const SessionsCancelParamsSchema = Type.Object(
  {
    childSessionKey: NonEmptyString,
    /** Free-form reason surfaced in logs and chat.final.statusReason. */
    reason: Type.Optional(Type.String({ maxLength: 280 })),
    /**
     * When true (default), broadcast a `chat.final` to the requester session so
     * the parent thread sees the cancel without a separate fetch. Set to false
     * for tests and quiet callers.
     */
    broadcast: Type.Optional(Type.Boolean()),
  },
  { additionalProperties: false },
);
export type SessionsCancelParams = Static<typeof SessionsCancelParamsSchema>;

export const SessionsCancelResultSchema = Type.Object(
  {
    cancelled: Type.Boolean(),
    childSessionKey: NonEmptyString,
    runId: Type.Union([NonEmptyString, Type.Null()]),
    reason: Type.String(),
    /** True when a child run was located and finalized. */
    hadRun: Type.Boolean(),
    /** When true, parent was notified via chat.final with status: "cancelled". */
    broadcast: Type.Boolean(),
    /** When `cancelled: false`, this is the matching error code (e.g. INVALID_REQUEST). */
    errorCode: Type.Optional(NonEmptyString),
    message: Type.Optional(Type.String()),
  },
  { additionalProperties: false },
);
export type SessionsCancelResult = Static<typeof SessionsCancelResultSchema>;

/** Link a spawned subagent run to the requester's active dialogue turn tree. */
export const SessionsTurnsLinkChildParamsSchema = Type.Object(
  {
    requesterSessionKey: NonEmptyString,
    childRunId: NonEmptyString,
    childSessionKey: NonEmptyString,
    parentRunId: Type.Optional(NonEmptyString),
    label: Type.Optional(Type.String()),
  },
  { additionalProperties: false },
);
export type SessionsTurnsLinkChildParams = Static<typeof SessionsTurnsLinkChildParamsSchema>;

export const SessionsCompactParamsSchema = Type.Object(
  {
    key: NonEmptyString,
    maxLines: Type.Optional(Type.Integer({ minimum: 1 })),
  },
  { additionalProperties: false },
);

export const SessionsContextBreakdownParamsSchema = Type.Object(
  {
    key: NonEmptyString,
  },
  { additionalProperties: false },
);

export const SessionsContextBreakdownResultSchema = Type.Object(
  {
    breakdown: Type.Object({
      sessionKey: NonEmptyString,
      usedTokens: Type.Number(),
      limitTokens: Type.Union([Type.Number(), Type.Null()]),
      percent: Type.Union([Type.Number(), Type.Null()]),
      model: Type.Union([Type.String(), Type.Null()]),
      reportSource: Type.Union([Type.Literal("run"), Type.Literal("estimate")]),
      reportGeneratedAt: Type.Number(),
      categories: Type.Array(
        Type.Object({
          id: Type.String(),
          label: Type.String(),
          tokens: Type.Number(),
        }),
      ),
      subagentSessions: Type.Array(
        Type.Object({
          sessionKey: NonEmptyString,
          label: Type.Union([Type.String(), Type.Null()]),
          tokens: Type.Number(),
        }),
      ),
    }),
    report: Type.Unknown(),
  },
  { additionalProperties: false },
);

export const SessionsPalaceContinuityParamsSchema = Type.Object(
  {
    key: NonEmptyString,
    /** When true, run a semantic memory search (loads local embeddings — expensive). */
    includeMemorySnippets: Type.Optional(Type.Boolean()),
  },
  { additionalProperties: false },
);

export const SessionsPalaceContinuityClearParamsSchema = Type.Object(
  {
    key: NonEmptyString,
  },
  { additionalProperties: false },
);

export const SessionsPalaceContinuityRemoveParamsSchema = Type.Object(
  {
    key: NonEmptyString,
    pointId: NonEmptyString,
  },
  { additionalProperties: false },
);

export const SessionsPalaceContinuityAddParamsSchema = Type.Object(
  {
    key: NonEmptyString,
    text: NonEmptyString,
    category: Type.Optional(Type.Union([Type.Literal("decision"), Type.Literal("fact")])),
  },
  { additionalProperties: false },
);

export const SessionContinuityPointSchema = Type.Object({
  id: NonEmptyString,
  sessionId: NonEmptyString,
  timestamp: NonEmptyString,
  category: NonEmptyString,
  preview: Type.String(),
  body: Type.String(),
  path: NonEmptyString,
  startLine: Type.Integer({ minimum: 1 }),
  endLine: Type.Integer({ minimum: 1 }),
});

export const StructuredTaskStateSchema = Type.Object({
  taskId: NonEmptyString,
  description: NonEmptyString,
  category: Type.Union([
    Type.Literal("implementation"),
    Type.Literal("research"),
    Type.Literal("review"),
    Type.Literal("coordinating"),
    Type.Literal("general"),
  ]),
  status: Type.Union([
    Type.Literal("pending"),
    Type.Literal("assigned"),
    Type.Literal("in_progress"),
    Type.Literal("completed"),
    Type.Literal("failed"),
  ]),
  subTasks: Type.Array(
    Type.Object({
      subTaskId: NonEmptyString,
      description: Type.String(),
      status: Type.String(),
    }),
  ),
  workerAgentId: Type.Optional(Type.String()),
  lastUpdateAt: Type.Number(),
  findings: Type.Optional(Type.String()),
  constraint: Type.Optional(Type.String()),
});

export const SessionContinuityPayloadSchema = Type.Object({
  activeTask: Type.Optional(StructuredTaskStateSchema),
  pendingSubTasks: Type.Array(
    Type.Object({
      subTaskId: NonEmptyString,
      description: Type.String(),
      status: Type.String(),
    }),
  ),
  recentDecisions: Type.Array(
    Type.Object({
      id: NonEmptyString,
      content: Type.String(),
      timestamp: Type.Number(),
    }),
  ),
  constraints: Type.Array(
    Type.Object({
      text: Type.String(),
      source: NonEmptyString,
    }),
  ),
  lastWorkerFindings: Type.Optional(Type.String()),
});

export const PalaceMemorySnippetSchema = Type.Object({
  path: NonEmptyString,
  snippet: Type.String(),
  score: Type.Optional(Type.Number()),
});

export const SessionsPalaceContinuityResultSchema = Type.Object(
  {
    sessionKey: NonEmptyString,
    sessionId: NonEmptyString,
    points: Type.Array(SessionContinuityPointSchema),
    totalChunks: Type.Integer({ minimum: 0 }),
    backend: Type.Union([Type.String(), Type.Null()]),
    structuredContinuity: Type.Optional(SessionContinuityPayloadSchema),
    memorySnippets: Type.Optional(Type.Array(PalaceMemorySnippetSchema)),
  },
  { additionalProperties: false },
);

export const SessionsPalaceContinuityClearResultSchema = Type.Object(
  {
    sessionKey: NonEmptyString,
    sessionId: NonEmptyString,
    removedSections: Type.Integer({ minimum: 0 }),
    removedChunks: Type.Integer({ minimum: 0 }),
    removedThreads: Type.Integer({ minimum: 0 }),
  },
  { additionalProperties: false },
);

export const SessionsPalaceContinuityRemoveResultSchema = Type.Object(
  {
    sessionKey: NonEmptyString,
    sessionId: NonEmptyString,
    pointId: NonEmptyString,
    removed: Type.Boolean(),
    editedFile: Type.Optional(Type.String()),
  },
  { additionalProperties: false },
);

export const SessionsPalaceContinuityAddResultSchema = Type.Object(
  {
    sessionKey: NonEmptyString,
    sessionId: NonEmptyString,
    path: Type.String(),
    category: Type.Union([Type.Literal("decision"), Type.Literal("fact")]),
  },
  { additionalProperties: false },
);

export const SessionsUsageParamsSchema = Type.Object(
  {
    /** Specific session key to analyze; if omitted returns all sessions. */
    key: Type.Optional(NonEmptyString),
    /** Start date for range filter (YYYY-MM-DD). */
    startDate: Type.Optional(Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" })),
    /** End date for range filter (YYYY-MM-DD). */
    endDate: Type.Optional(Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" })),
    /** Maximum sessions to return (default 50). */
    limit: Type.Optional(Type.Integer({ minimum: 1 })),
    /** Include context weight breakdown (systemPromptReport). */
    includeContextWeight: Type.Optional(Type.Boolean()),
  },
  { additionalProperties: false },
);
